import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';

/**
 * A simple custom hook that "types" and "deletes" text.
 * Adjust typingSpeed, deletingSpeed, and pauseTime as desired.
 */
function useTypewriter(
  text,
  typingSpeed = 80,
  deletingSpeed = 40,
  pauseTime = 150
) {
  const [typedText, setTypedText] = useState('');
  const [index, setIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const handleTyping = () => {
      if (!isDeleting) {
        // Typing forward
        const nextText = text.slice(0, index + 1);
        setTypedText(nextText);
        setIndex((prev) => prev + 1);

        if (nextText === text) {
          timer = setTimeout(() => setIsDeleting(true), pauseTime);
        } else {
          timer = setTimeout(handleTyping, typingSpeed);
        }
      } else {
        // Deleting
        const nextText = text.slice(0, index - 1);
        setTypedText(nextText);
        setIndex((prev) => prev - 1);

        if (nextText === '') {
          timer = setTimeout(() => setIsDeleting(false), pauseTime);
        } else {
          timer = setTimeout(handleTyping, deletingSpeed);
        }
      }
    };

    timer = setTimeout(handleTyping, isDeleting ? deletingSpeed : typingSpeed);
    return () => clearTimeout(timer);
  }, [index, isDeleting, text, typingSpeed, deletingSpeed, pauseTime]);

  return typedText;
}

// Markdown components configuration
const markdownComponents = {
  p: ({ node, ...props }) => (
    <Typography component="span" variant="body1" sx={{ display: 'block', mb: 1 }}>
      {props.children}
    </Typography>
  ),
  a: ({ node, href, ...props }) => {
    const isInternal = href?.startsWith('/');
    if (isInternal) {
      return (
        <RouterLink
          to={href}
          style={{ color: '#1abc9c', textDecoration: 'underline' }}
        >
          {props.children}
        </RouterLink>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1abc9c', textDecoration: 'underline' }}
        {...props}
      />
    );
  },
  li: ({ node, ...props }) => (
    <Typography component="li" variant="body1" sx={{ ml: 2 }}>
      {props.children}
    </Typography>
  ),
  ul: ({ node, ...props }) => (
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {props.children}
    </Box>
  ),
  ol: ({ node, ...props }) => (
    <Box component="ol" sx={{ m: 0, pl: 2 }}>
      {props.children}
    </Box>
  ),
  strong: ({ node, ...props }) => (
    <Box component="strong" sx={{ fontWeight: 'bold' }}>
      {props.children}
    </Box>
  ),
};

const Hero = ({
  title1 = 'Relax. ',
  title2 = 'Let robots work',
  contentTitle = 'Leverage your company’s knowledge and streamline communication effortlessly.',
  contentText = `Make your business accessible 24/7 with AI-powered automation—seamlessly handling calls and messages across phone, email, social media, messengers, and your website while delivering relevant content across multiple channels.`,
  image = '/path/to/your/hero-image.jpg',
}) => {
  // Use the custom typewriter effect for title2
  const typedTitle2 = useTypewriter(title2);
  const themeM = useTheme();
  const isMobile = useMediaQuery(themeM.breakpoints.down('md'));

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        mt: '64px', // offset to account for fixed header
        // DESKTOP (md+): fill screen minus header
        [theme.breakpoints.up('md')]: {
          position: 'relative',
          height: 'calc(100vh - 64px)', // adjust height to subtract header
          overflow: 'hidden',
        },
        // MOBILE (below md): let content flow naturally
        [theme.breakpoints.down('md')]: {
          position: 'relative',
          height: 'auto',
        },
      })}
    >
      {/** 1) MAIN TITLE (always first in DOM; on mobile it appears first) */}
      <Box
        sx={(theme) => ({
          zIndex: 2,
          color: '#fff',
          // Desktop
          [theme.breakpoints.up('md')]: {
            position: 'absolute',
            top: '20%',
            left: '10%',
          },
          // Mobile
          [theme.breakpoints.down('md')]: {
            position: 'absolute',
            top: '10px',
            left: '10%',
            right: '10%',
          },
        })}
      >
        <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 'bold', mb: 2 }}>
          <Box
            component="span"
            sx={(theme) => ({ color: theme.palette.secondary.main })}
          >
            {title1}
          </Box>
          <Box
            component="span"
            sx={(theme) => ({ 
              //color: theme.palette.primary.main,
              display: { xs: 'block', md: 'inline' }
            })}
          >
            {typedTitle2}
          </Box>
        </Typography>
      </Box>

      {/** 2) THE IMAGE (in normal flow on mobile, absolutely behind on desktop) */}
      <Box
        component="img"
        src={image}
        alt="Hero Background"
        sx={(theme) => ({
          // Desktop
          [theme.breakpoints.up('md')]: {
            position: 'absolute',
            top: 0,
            right: 0,
            width: 'auto',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1, // behind the text
          },
          // Mobile: image now in normal flow
          [theme.breakpoints.down('md')]: {
            position: 'static',
            width: '100%',
            height: '50vh', // half of the viewport height
            objectFit: 'cover',
            zIndex: 0,
          },
        })}
      />

      {/** 3) THE OTHER TEXT (on desktop, also absolutely over image; on mobile, below image) */}
      <Box
        sx={(theme) => ({
          zIndex: 2,
          color: '#fff',
          maxWidth: 500,
          // Desktop
          [theme.breakpoints.up('md')]: {
            position: 'absolute',
            top: 'calc(20% + 100px)', // adjust as needed
            left: '10%',
          },
          // Mobile
          [theme.breakpoints.down('md')]: {
            position: 'relative',
            mt: 2,
            ml: 2,
            mr: 2,
            mb: 4,
          },
        })}
      >
        <Box sx={{ '& > *:last-child': { mb: 0 } }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            <ReactMarkdown components={markdownComponents}>
              {contentTitle}
            </ReactMarkdown>
          </Typography>
          <ReactMarkdown components={markdownComponents}>
            {contentText}
          </ReactMarkdown>
        </Box>
      </Box>
    </Box>
  );
};

export default Hero;